import React, { Component, ReactNode } from "react";
import { classNames } from "primereact/utils";

/* ------------------------------------------------------------------ */
/*  AppCard – drop-in replacement for Reactstrap Card / CardBody / CardHeader
/*  Usage:
/*    <AppCard>
/*      <AppCard.Header title="Title" />
/*      <AppCard.Body> ... </AppCard.Body>
/*    </AppCard>
/* ------------------------------------------------------------------ */

interface AppCardProps {
    children?: ReactNode;
    className?: string;
    /** Remove default padding from body (useful when body handles its own padding) */
    noPadding?: boolean;
    onClick?: () => void;
}

interface AppCardHeaderProps {
    children?: ReactNode;
    className?: string;
    title?: string;
    /** Right-side actions (buttons, etc.) */
    actions?: ReactNode;
}

interface AppCardBodyProps {
    children?: ReactNode;
    className?: string;
}

/* ----------  Card Body  ---------- */
class AppCardBody extends Component<AppCardBodyProps> {
    render() {
        const { children, className } = this.props;
        return (
            <div className={classNames("px-6 py-4", className)}>
                {children}
            </div>
        );
    }
}

/* ----------  Card Header  ---------- */
class AppCardHeader extends Component<AppCardHeaderProps> {
    render() {
        const { children, className, title, actions } = this.props;
        return (
            <div
                className={classNames(
                    "px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-3",
                    className,
                )}
            >
                {title ? (
                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                        {title}
                    </h3>
                ) : (
                    <div className="flex items-center gap-2 text-base font-semibold text-neutral-900 dark:text-neutral-100">
                        {children}
                    </div>
                )}
                {actions && <div className="flex items-center gap-2">{actions}</div>}
            </div>
        );
    }
}

/* ----------  Card Root  ---------- */
class AppCard extends Component<AppCardProps> {
    static Header = AppCardHeader;
    static Body = AppCardBody;

    render() {
        const { children, className, noPadding, onClick } = this.props;
        return (
            <div
                onClick={onClick}
                className={classNames(
                    "bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-minimal transition-all duration-200 overflow-hidden",
                    { "cursor-pointer": !!onClick },
                    className,
                )}
            >
                {children}
            </div>
        );
    }
}

export default AppCard;
